import React, { useState } from "react";
import { useSite } from "../../context/SiteContext";
import { Play, ArrowDown, MapPin } from "lucide-react";

const Hero = () => {
  const { profile, heroReel, clientLogos } = useSite();
  const [playing, setPlaying] = useState(false);

  const year = new Date().getFullYear();

  return (
    <section
      id="top"
      className="relative min-h-[100svh] w-full overflow-hidden bg-[#0a0a0a] grain"
    >
      {/* Cinematic backdrop */}
      <div className="absolute inset-0">
        {!playing && (
          <>
            <img
              src={`https://i.ytimg.com/vi/${heroReel.youtubeId}/maxresdefault.jpg`}
              alt="Showreel still"
              className="absolute inset-0 w-full h-full object-cover opacity-[0.28]"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/60 via-[#0a0a0a]/75 to-[#0a0a0a]" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_transparent_0%,_#0a0a0a_75%)]" />
          </>
        )}
        {playing && (
          <iframe
            className="absolute inset-0 w-full h-full"
            src={`https://www.youtube.com/embed/${heroReel.youtubeId}?autoplay=1&rel=0&modestbranding=1`}
            title={heroReel.title}
            frameBorder="0"
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
          />
        )}
      </div>

      {/* Top meta bar */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-28 md:pt-32">
        <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.24em] text-stone-400">
          <span className="flex items-center gap-2">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-300 animate-pulse" />
            Available for select projects — {year}
          </span>
          <span className="hidden sm:flex items-center gap-2">
            <MapPin size={12} /> {profile.location}
          </span>
        </div>
      </div>

      {/* Main hero text */}
      {!playing && (
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-28">
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-amber-300/90 mb-6">
            Creative Director · Cinematic Content &amp; AI Production
          </p>
          <h1 className="font-display text-stone-50 leading-[0.92] tracking-tight text-[54px] sm:text-[82px] md:text-[120px] lg:text-[150px]">
            Daniel
            <br />
            <span className="italic text-stone-200">Flynn</span>
            <span className="text-amber-300">.</span>
          </h1>

          <div className="mt-10 md:mt-14 grid md:grid-cols-12 gap-8">
            <p className="md:col-span-6 text-stone-300 text-lg md:text-xl leading-relaxed max-w-xl">
              I direct cinematic content and build AI-native production systems —
              automating the slow parts so the craft can move faster, from
              pitch to picture-lock. Dublin-based.
            </p>

            <div className="md:col-span-6 md:col-start-8 flex md:justify-end items-start">
              <button
                onClick={() => setPlaying(true)}
                className="group inline-flex items-center gap-4 text-stone-100"
              >
                <span className="relative w-16 h-16 md:w-20 md:h-20 rounded-full border border-stone-300/70 flex items-center justify-center transition-colors group-hover:bg-stone-50 group-hover:border-stone-50">
                  <Play
                    size={20}
                    className="ml-0.5 text-stone-100 group-hover:text-zinc-950 fill-current"
                  />
                  <span className="absolute inset-0 rounded-full border border-amber-300/40 animate-ping opacity-60" />
                </span>
                <span className="text-left">
                  <span className="block font-display text-xl md:text-2xl">
                    Play Trailer
                  </span>
                  <span className="block text-[11px] uppercase tracking-[0.22em] text-stone-400">
                    {heroReel.client} · {heroReel.title}
                  </span>
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {playing && (
        <button
          onClick={() => setPlaying(false)}
          className="absolute top-24 right-6 md:right-10 z-20 text-[11px] uppercase tracking-[0.22em] text-stone-100 border border-stone-100/70 px-3 py-1.5 hover:bg-stone-100 hover:text-zinc-950 transition-colors"
        >
          Close trailer
        </button>
      )}

      {/* Bottom ticker */}
      {!playing && (
        <div className="absolute bottom-0 left-0 right-0 z-10 border-t border-zinc-900/80 bg-[#0a0a0a]/60 backdrop-blur-sm">
          <div className="overflow-hidden py-4">
            <div className="marquee-track whitespace-nowrap font-display text-stone-300/90 text-2xl md:text-3xl flex items-center gap-10">
              {Array.from({ length: 2 }).flatMap((_, k) =>
                (clientLogos || []).map((t, i) => (
                  <span key={`${k}-${i}`} className="flex items-center gap-10">
                    <span className="italic opacity-70">{t}</span>
                    <span className="text-amber-300/80">—</span>
                  </span>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {!playing && (
        <a
          href="#work"
          onClick={(e) => {
            e.preventDefault();
            document
              .getElementById("work")
              ?.scrollIntoView({ behavior: "smooth" });
          }}
          className="hidden md:flex absolute right-10 bottom-24 z-10 flex-col items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-stone-400 hover:text-stone-100"
        >
          <span>Selected Work</span>
          <ArrowDown size={14} />
        </a>
      )}
    </section>
  );
};

export default Hero;
